import type {
  OblivionMineHit,
  OblivionMineMine,
  OblivionMinePlayerId,
  OblivionMineState,
} from "@cloudnest/oblivion-mine-engine";
import {
  isStartCell,
  OBLIVION_MINE_BOARD_SIZE,
} from "@cloudnest/oblivion-mine-engine";

interface OblivionMineBoardViewProps {
  readonly state: OblivionMineState;
  /** Mines to render: the masked observer view by default, or all (recap). */
  readonly mines: readonly OblivionMineMine[];
  readonly lastHit?: OblivionMineHit | null;
}

const SIZE = OBLIVION_MINE_BOARD_SIZE;

interface MineCell {
  readonly armedOwners: ReadonlySet<OblivionMinePlayerId>;
  readonly triggered: boolean;
}

function buildMineMap(
  mines: readonly OblivionMineMine[],
): Map<string, MineCell> {
  const map = new Map<
    string,
    { armedOwners: Set<OblivionMinePlayerId>; triggered: boolean }
  >();
  for (const mine of mines) {
    const key = `${mine.row}-${mine.col}`;
    const entry = map.get(key) ?? { armedOwners: new Set(), triggered: false };
    if (mine.triggered) entry.triggered = true;
    else entry.armedOwners.add(mine.owner);
    map.set(key, entry);
  }
  return map;
}

function pieceLabel(playerId: OblivionMinePlayerId): string {
  return playerId === "p1" ? "흑 말" : "백 말";
}

/**
 * Read-only 11×11 board renderer for 망각의 지뢰. Renders treasures, both start
 * corners, the two pieces, and whichever mines are passed in (the masked
 * observer view, or every mine in recap mode), plus the last hit. Pure
 * presentation — drives off engine state only, no interactivity.
 */
export function OblivionMineBoardView({
  state,
  mines,
  lastHit,
}: OblivionMineBoardViewProps) {
  const mineMap = buildMineMap(mines);
  const cells: React.ReactNode[] = [];

  for (let row = 0; row < SIZE; row += 1) {
    for (let col = 0; col < SIZE; col += 1) {
      const treasure = state.treasures.find(
        (t) => t.row === row && t.col === col,
      );
      const start = isStartCell(row, col);
      const piecesHere = (["p1", "p2"] as const).filter(
        (playerId) =>
          state.positions[playerId].row === row &&
          state.positions[playerId].col === col,
      );
      const mine = mineMap.get(`${row}-${col}`);
      const isHit =
        lastHit !== undefined && lastHit !== null
          ? lastHit.row === row && lastHit.col === col
          : false;

      const mineState = mine
        ? mine.triggered
          ? "triggered"
          : "armed"
        : "none";
      const treasureState = treasure
        ? (treasure.collectedBy ?? "uncollected")
        : "none";
      const pieceState =
        piecesHere.length === 2 ? "both" : (piecesHere[0] ?? "none");

      const background = treasure
        ? "var(--cn-oblivion-mine-treasure)"
        : start
          ? "var(--cn-oblivion-mine-start)"
          : (row + col) % 2 === 0
            ? "var(--cn-oblivion-mine-cell)"
            : "var(--cn-oblivion-mine-cell-alt)";

      cells.push(
        <div
          key={`${row}-${col}`}
          data-testid={`oblivion-mine-cell-${row}-${col}`}
          data-piece={pieceState}
          data-treasure={treasureState}
          data-mine={mineState}
          data-start={start ? "true" : undefined}
          className="relative flex items-center justify-center"
          style={{
            background,
            boxShadow: isHit
              ? "inset 0 0 0 3px var(--cn-oblivion-mine-last-hit)"
              : "inset 0 0 0 0.5px var(--cn-oblivion-mine-grid-line)",
            outline: treasure
              ? "2px solid var(--cn-oblivion-mine-treasure-ring)"
              : undefined,
            outlineOffset: treasure ? "-2px" : undefined,
            opacity:
              treasure && treasure.collectedBy !== null ? 0.55 : undefined,
          }}
        >
          {/* Mine marker (under the piece). */}
          {mine && (
            <span
              aria-hidden
              className="absolute rounded-full"
              style={{
                top: "10%",
                right: "10%",
                width: "26%",
                height: "26%",
                background: mine.triggered
                  ? "var(--cn-oblivion-mine-mine-triggered)"
                  : mine.armedOwners.has("p1")
                    ? "var(--cn-oblivion-mine-mine-p1)"
                    : "var(--cn-oblivion-mine-mine-p2)",
                boxShadow: "0 0 0 1px rgba(0,0,0,0.35)",
              }}
            />
          )}

          {/* Treasure marker. */}
          {treasure && piecesHere.length === 0 && (
            <span
              role="img"
              aria-label={treasure.collectedBy ? "획득한 보물" : "보물"}
              style={{ fontSize: "min(3vw, 16px)" }}
            >
              💎
            </span>
          )}

          {/* Piece token(s) — highest layer. */}
          {piecesHere.length > 0 && (
            <span className="flex items-center justify-center gap-0.5">
              {piecesHere.map((playerId) => (
                <span
                  key={playerId}
                  role="img"
                  aria-label={pieceLabel(playerId)}
                  className="flex items-center justify-center rounded-full font-bold"
                  style={{
                    width: piecesHere.length === 2 ? "44%" : "78%",
                    height: piecesHere.length === 2 ? "70%" : "78%",
                    aspectRatio: "1 / 1",
                    fontSize: "min(2.6vw, 13px)",
                    color:
                      playerId === "p1"
                        ? "var(--cn-oblivion-mine-piece-p2)"
                        : "var(--cn-oblivion-mine-piece-p1)",
                    background:
                      playerId === "p1"
                        ? "var(--cn-oblivion-mine-piece-p1)"
                        : "var(--cn-oblivion-mine-piece-p2)",
                    boxShadow: `0 0 0 2px ${
                      playerId === "p1"
                        ? "var(--cn-oblivion-mine-piece-p1-ring)"
                        : "var(--cn-oblivion-mine-piece-p2-ring)"
                    }`,
                  }}
                >
                  {playerId === "p1" ? "흑" : "백"}
                </span>
              ))}
            </span>
          )}
        </div>,
      );
    }
  }

  return (
    <div
      data-testid="oblivion-mine-board"
      className="grid w-full select-none rounded-md"
      style={{
        aspectRatio: "1 / 1",
        gridTemplateColumns: `repeat(${SIZE}, 1fr)`,
        gridTemplateRows: `repeat(${SIZE}, 1fr)`,
        background: "var(--cn-oblivion-mine-board-bg)",
        padding: "2px",
        gap: "1px",
        maxWidth: "min(92vw, 560px)",
      }}
    >
      {cells}
    </div>
  );
}
